import { colourName, hsvToRgb, hueDistance, rgbToHsv } from './colorUtils'

const EMPTY = { matched: false, coverage: 0, center: null, box: null }

// Defaults for the RGB colour measurement (contract 18.2.2). Tolerance is an
// RGB Euclidean distance (0 = exact, 441 = black↔white). Coverage is the share
// of sampled pixels that must be within tolerance. Consecutive is how many
// frames in a row must match before a rule fires. Step samples every Nth pixel.
export const COLOUR_DEFAULTS = {
    tolerance: 60,
    coverage: 0.4,
    consecutive: 3,
    step: 2,
}

// Largest possible RGB distance, black (0,0,0) to white (255,255,255).
export const MAX_RGB_DISTANCE = Math.sqrt(3 * 255 * 255)

const clampNum = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v)

// Kid-friendly "intensity" is just the RGB tolerance expressed as a 0-100%
// closeness: 100% means an exact colour match (tolerance 0), lower % allows
// more different shades. These convert between the two so the runtime keeps
// using a plain RGB distance while the UI only ever shows a percentage.
export const toleranceToIntensity = (tol) =>
    Math.round(100 * (1 - clampNum(tol, 0, MAX_RGB_DISTANCE) / MAX_RGB_DISTANCE))

export const intensityToTolerance = (pct) =>
    Math.round((MAX_RGB_DISTANCE * (100 - clampNum(pct, 0, 100))) / 100)

// How closely the measured colour matches the target, as a 0-100% intensity —
// the live number shown for the selected colour in Execute.
export const distanceToIntensity = (distance) =>
    Math.max(
        0,
        Math.round(100 * (1 - clampNum(distance, 0, MAX_RGB_DISTANCE) / MAX_RGB_DISTANCE))
    )

// RGB colour measurement exactly as defined in contract section 18.2:
//   1. read the pixels inside the region of interest (whole frame if none),
//   2. average R, G, B across the sampled pixels (alpha ignored),
//   3. distance d = √((ΔR)² + (ΔG)² + (ΔB)²) between that mean and the target,
//   4. a match needs d ≤ tolerance AND the share of individual pixels within
//      tolerance ≥ the coverage threshold.
// No hue/saturation model, no white-balance — a direct RGB comparison. The
// consecutive-frame requirement is applied by the caller (this is per-frame).
export function detectColourRGB(frame, width, height, target, region) {
    const data = frame.data
    const [tr, tg, tb] = target.rgb
    const tol = target.tolerance ?? COLOUR_DEFAULTS.tolerance
    const step = Math.max(
        1,
        Math.min(8, Math.round(target.step ?? COLOUR_DEFAULTS.step))
    )
    const covThreshold = target.coverage ?? COLOUR_DEFAULTS.coverage

    const x0 = Math.max(0, Math.floor((region?.x ?? 0) * width))
    const y0 = Math.max(0, Math.floor((region?.y ?? 0) * height))
    const x1 = Math.min(
        width,
        Math.ceil(((region?.x ?? 0) + (region?.w ?? 1)) * width)
    )
    const y1 = Math.min(
        height,
        Math.ceil(((region?.y ?? 0) + (region?.h ?? 1)) * height)
    )

    let sumR = 0
    let sumG = 0
    let sumB = 0
    let sampled = 0
    let within = 0

    for (let y = y0; y < y1; y += step) {
        for (let x = x0; x < x1; x += step) {
            const i = (y * width + x) * 4
            const r = data[i]
            const g = data[i + 1]
            const b = data[i + 2]
            sumR += r
            sumG += g
            sumB += b
            sampled++
            const dr = r - tr
            const dg = g - tg
            const db = b - tb
            if (Math.sqrt(dr * dr + dg * dg + db * db) <= tol) within++
        }
    }

    if (sampled === 0) {
        return {
            matched: false,
            distance: MAX_RGB_DISTANCE,
            coverage: 0,
            meanRgb: [0, 0, 0],
        }
    }

    const meanR = Math.round(sumR / sampled)
    const meanG = Math.round(sumG / sampled)
    const meanB = Math.round(sumB / sampled)
    const dr = meanR - tr
    const dg = meanG - tg
    const db = meanB - tb
    const distance = Math.sqrt(dr * dr + dg * dg + db * db)
    const coverage = within / sampled

    return {
        matched: distance <= tol && coverage >= covThreshold,
        distance,
        coverage,
        meanRgb: [meanR, meanG, meanB],
    }
}

export function detectColour(frame, width, height, target, region) {
    const [th, ts, tv] = rgbToHsv(target.rgb[0], target.rgb[1], target.rgb[2])
    const data = frame.data

    const x0 = Math.max(0, Math.floor((region?.x ?? 0) * width))
    const y0 = Math.max(0, Math.floor((region?.y ?? 0) * height))
    const x1 = Math.min(width, Math.ceil(((region?.x ?? 0) + (region?.w ?? 1)) * width))
    const y1 = Math.min(height, Math.ceil(((region?.y ?? 0) + (region?.h ?? 1)) * height))

    let hits = 0
    let sumX = 0
    let sumY = 0
    let minX = width
    let minY = height
    let maxX = 0
    let maxY = 0

    const lowSaturationTarget = ts < 0.25 || tv < 0.2

    for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
            const i = (y * width + x) * 4
            const [h, s, v] = rgbToHsv(data[i], data[i + 1], data[i + 2])
            let match
            if (lowSaturationTarget) {
                match = s <= target.satMin + 0.15 && Math.abs(v - tv) <= 0.18
            } else {
                match =
                    s >= target.satMin &&
                    v >= target.valMin &&
                    hueDistance(h, th) <= target.hueTolerance
            }
            if (match) {
                hits++
                sumX += x
                sumY += y
                if (x < minX) minX = x
                if (y < minY) minY = y
                if (x > maxX) maxX = x
                if (y > maxY) maxY = y
            }
        }
    }

    const area = Math.max(1, (x1 - x0) * (y1 - y0))
    const coverage = hits / area
    if (hits === 0) return { ...EMPTY }

    return {
        matched: coverage >= target.minCoverage,
        coverage,
        center: { x: sumX / hits / width, y: sumY / hits / height },
        box: {
            x: minX / width,
            y: minY / height,
            w: (maxX - minX) / width,
            h: (maxY - minY) / height,
        },
    }
}

// Representative swatch colour for each name colourName() can return, so the
// live colour-share readout can show a chip next to each percentage.
export const NAME_SWATCH = {
    Red: '#dc2828',
    Orange: '#f08c1e',
    Yellow: '#f0dc28',
    Green: '#28b446',
    Cyan: '#28c8d2',
    Blue: '#285adc',
    Purple: '#9640d2',
    Magenta: '#dc32b4',
    White: '#f2f2f2',
    Gray: '#888888',
    Black: '#202020',
}

// Mean colour of the frame (or ROI), as an [r,g,b] triplet 0-255. Used for the
// play-mode "sample this colour" readout so a user can read a real hex off the
// scene and paste it into a decision rule.
export function averageColour(frame, width, height, step = 2, region) {
    const data = frame.data
    const s = Math.max(1, Math.min(8, Math.round(step)))
    const x0 = Math.max(0, Math.floor((region?.x ?? 0) * width))
    const y0 = Math.max(0, Math.floor((region?.y ?? 0) * height))
    const x1 = Math.min(
        width,
        Math.ceil(((region?.x ?? 0) + (region?.w ?? 1)) * width)
    )
    const y1 = Math.min(
        height,
        Math.ceil(((region?.y ?? 0) + (region?.h ?? 1)) * height)
    )
    let r = 0
    let g = 0
    let b = 0
    let n = 0
    for (let y = y0; y < y1; y += s) {
        for (let x = x0; x < x1; x += s) {
            const i = (y * width + x) * 4
            r += data[i]
            g += data[i + 1]
            b += data[i + 2]
            n++
        }
    }
    if (!n) return [0, 0, 0]
    return [Math.round(r / n), Math.round(g / n), Math.round(b / n)]
}

// Overall colour composition of the frame: classify every sampled pixel into a
// named colour bucket and return each bucket's share, so the UI can show
// "Red 42%, Blue 21%, …" as bars that auto-adjust to whatever the camera sees.
// Shares sum to ~1. Sorted most-present first. Neutrals (white/gray/black) are
// included so the shares always add up.
export function colourShares(frame, width, height, step = 2) {
    const data = frame.data
    const s = Math.max(1, Math.min(8, Math.round(step)))
    const counts = {}
    let total = 0
    for (let y = 0; y < height; y += s) {
        for (let x = 0; x < width; x += s) {
            const i = (y * width + x) * 4
            const [h, sat, val] = rgbToHsv(data[i], data[i + 1], data[i + 2])
            const name = colourName(h, sat, val)
            counts[name] = (counts[name] || 0) + 1
            total++
        }
    }
    if (!total) return []
    return Object.entries(counts)
        .map(([name, c]) => ({
            name,
            hex: NAME_SWATCH[name] || '#888888',
            share: c / total,
        }))
        .sort((a, b) => b.share - a.share)
}

const HUE_BUCKETS = 24

export function dominantColour(frame, width, height) {
    const data = frame.data
    const buckets = new Float32Array(HUE_BUCKETS)
    for (let i = 0; i < data.length; i += 4) {
        const [h, s, v] = rgbToHsv(data[i], data[i + 1], data[i + 2])
        if (s < 0.35 || v < 0.25) continue
        const b = Math.min(HUE_BUCKETS - 1, Math.floor((h / 360) * HUE_BUCKETS))
        buckets[b] += s * v
    }
    let peak = 0
    let peakWeight = 0
    for (let b = 0; b < HUE_BUCKETS; b++) {
        if (buckets[b] > peakWeight) {
            peakWeight = buckets[b]
            peak = b
        }
    }
    if (peakWeight === 0) return null
    const hue = (peak + 0.5) * (360 / HUE_BUCKETS)
    const rgb = hsvToRgb(hue, 1, 1)
    return { hue, rgb, name: colourName(hue, 1, 1) }
}
